import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Waves, Sparkles, Lock, Check, X, User, Users, Eye, EyeOff } from 'lucide-react';
import { APP_NAME, APP_TAGLINE } from '../constants/appName';
import CountrySelect from '../components/CountrySelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const AuthPage = () => {
  const { login, register } = useAuth();
  const { t } = useI18n();
  // Honour ?mode=login|register from the WelcomePage CTAs so the right tab
  // is preselected on landing.
  const [params] = useSearchParams();
  const initialTab = params.get('mode') === 'register' ? 'register' : 'login';
  const [loading, setLoading] = useState(false);
  const [registerType, setRegisterType] = useState('guest'); // 'guest' or 'animator' (labelled "Staff Member" in UI — covers admin/chief/animator/kids/limited_editor via the access code's `code_type`)
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'guest',
    nationality: '',
    room_number: '',
    check_in_date: '',
    check_out_date: '',
    repeater_guest: false,
    access_code: '',
    // Guest registration v2 — one room account manages bookings for everyone
    main_guest_name: '',
    party_size: 1,
    occupants: [{ name: '', category: 'adult' }],
  });
  const [codeValidation, setCodeValidation] = useState({ checked: false, valid: false, message: '', target_email: null, hotel_name: null, hotel_id: null, code_type: null });
  // JF-03: per-field validation errors (check-in / check-out mandatory for guests)
  const [fieldErrors, setFieldErrors] = useState({});

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(loginData.email, loginData.password);
    if (!result.success) {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // Only validate access code for animators
    if (registerType === 'animator' && !codeValidation.valid) {
      toast.error('Please enter and validate your access code first');
      return;
    }

    // JF-03: mandatory check-in / check-out dates for GUEST signup. Inline
    // red error under the missing field + blocking toast.
    if (registerType === 'guest') {
      const errs = {};
      if (!registerData.check_in_date) errs.check_in_date = 'Check-in date is required';
      if (!registerData.check_out_date) errs.check_out_date = 'Check-out date is required';
      if (registerData.check_in_date && registerData.check_out_date &&
          registerData.check_out_date < registerData.check_in_date) {
        errs.check_out_date = 'Check-out must be after check-in';
      }
      if (Object.keys(errs).length) {
        setFieldErrors(errs);
        toast.error('Please fill in your stay dates to complete signup.');
        return;
      }
    }
    setFieldErrors({});

    setLoading(true);
    
    // Call register - with access code only for animators
    try {
      const url = registerType === 'animator' 
        ? `${API}/auth/register?access_code=${registerData.access_code}`
        : `${API}/auth/register`;
      
      const cleanOccupants = (registerData.occupants || [])
        .map(o => ({ name: (o.name || '').trim(), category: o.category || 'adult' }))
        .filter(o => o.name);
      const response = await axios.post(url, {
        email: registerData.email,
        password: registerData.password,
        full_name: registerData.full_name,
        role: registerType, // Use the selected register type
        nationality: registerData.nationality,
        room_number: registerData.room_number,
        check_in_date: registerData.check_in_date || null,
        check_out_date: registerData.check_out_date || null,
        repeater_guest: registerData.repeater_guest,
        // Guest v2 — only send when registering as guest
        ...(registerType === 'guest' ? {
          main_guest_name: registerData.main_guest_name || registerData.full_name,
          party_size: cleanOccupants.length || Number(registerData.party_size) || 1,
          occupants: cleanOccupants,
        } : {}),
      });
      
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Trigger a page reload to update auth context
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
    
    setLoading(false);
  };

  const validateAccessCode = async () => {
    if (!registerData.access_code) {
      toast.error('Please enter an access code');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/access-codes/validate`, {
        code: registerData.access_code
      });
      
      if (response.data.valid) {
        setCodeValidation({
          checked: true,
          valid: true,
          message: response.data.message,
          target_email: response.data.target_email || null,
          hotel_name: response.data.hotel_name || null,
          hotel_id: response.data.hotel_id || null,
          code_type: response.data.code_type || null,
        });
        // Feb 2026 · Multi-hotel staff email architecture. If the code
        // is bound to a specific email (Owner-generated Hotel Admin
        // invite = `admin.{hotel_slug}@resort.com`), prefill and lock
        // the email + full name fields so the registrant only has to
        // pick a password.
        if (response.data.target_email) {
          setRegisterData((r) => ({
            ...r,
            email: response.data.target_email,
            full_name: response.data.hotel_name || r.full_name,
          }));
          toast.success(`Code verified — signing up as ${response.data.target_email}`);
        } else {
          toast.success('Access code verified!');
        }
      } else {
        setCodeValidation({ checked: true, valid: false, message: response.data.message, target_email: null, hotel_name: null, hotel_id: null, code_type: null });
        toast.error(response.data.message);
      }
    } catch (error) {
      setCodeValidation({ checked: true, valid: false, message: 'Code validation failed', target_email: null, hotel_name: null, hotel_id: null, code_type: null });
      toast.error('Failed to validate code');
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden gradient-splash flex items-center justify-center px-4 py-8">
      {/* Decorative tropical blobs */}
      <div aria-hidden className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[#00B4D8]/35 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-[#FFB703]/20 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute top-1/3 right-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />

      <div className="relative w-full max-w-md animate-slide-up">
        {/* Back to welcome */}
        <a
          href="/"
          className="absolute -top-2 left-0 text-xs text-white/85 hover:text-white inline-flex items-center gap-1"
          data-testid="auth-back-welcome"
        >
          ← Welcome
        </a>
        <div className="text-center mb-7">
          <img
            src="/logo-wide.png"
            alt={APP_NAME}
            className="mx-auto h-24 md:h-28 w-auto drop-shadow-2xl mb-3 select-none animate-splash-pulse"
            draggable={false}
            data-testid="auth-page-logo"
          />
          <p className="text-white/90 text-base font-medium tracking-wide">{APP_TAGLINE}</p>
        </div>

        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4 bg-white/15 backdrop-blur-xl border border-white/20 rounded-full p-1 h-auto">
            <TabsTrigger
              value="login"
              data-testid="login-tab"
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#0A3055] data-[state=active]:shadow-lg text-white/90 font-bold py-2 transition-all"
            >{t('login')}</TabsTrigger>
            <TabsTrigger
              value="register"
              data-testid="register-tab"
              className="rounded-full data-[state=active]:bg-white data-[state=active]:text-[#0A3055] data-[state=active]:shadow-lg text-white/90 font-bold py-2 transition-all"
            >{t('register')}</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <Card className="border-0 shadow-2xl rounded-3xl bg-white/95 backdrop-blur-md">
              <CardHeader className="pb-3 pt-7 px-7">
                <CardTitle className="font-heading text-2xl">{t('welcomeTo')} IC FunFlow</CardTitle>
                <CardDescription>{t('signInSubtitle')}</CardDescription>
              </CardHeader>
              <CardContent className="px-7 pb-7">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">{t('email')}</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      className="mt-1.5 h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password">{t('password')}</Label>
                    <div className="relative mt-1.5">
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type={showLoginPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        className="pr-10 h-11"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="login-toggle-password"
                      >
                        {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    data-testid="login-submit-btn"
                    className="w-full h-12 font-bold text-base"
                    size="lg"
                    disabled={loading}
                  >
                    {loading ? t('signingIn') : t('signIn')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="register">
            <Card className="border-0 shadow-2xl rounded-3xl bg-white/95 backdrop-blur-md">
              <CardHeader className="pb-3 pt-7 px-7">
                <CardTitle className="flex items-center gap-2 font-heading text-2xl">
                  <Sparkles className="w-6 h-6 text-[#FFB703]" />
                  {t('joinUs')}
                </CardTitle>
                <CardDescription>{t('createAccountSubtitle')}</CardDescription>
              </CardHeader>
              <CardContent className="px-7 pb-7">
                <form onSubmit={handleRegister} className="space-y-4">
                  {/* Role Selection */}
                  <div className="mb-4">
                    <Label className="mb-2 block">{t('iAmA')}</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setRegisterType('guest');
                          setCodeValidation({ checked: false, valid: false, message: '', target_email: null, hotel_name: null, hotel_id: null, code_type: null });
                        }}
                        data-testid="register-type-guest"
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                          registerType === 'guest'
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-border hover:border-primary/30'
                        }`}
                      >
                        <User className="w-5 h-5" />
                        <span className="font-medium">{t('guest')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setRegisterType('animator')}
                        data-testid="register-type-animator"
                        className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                          registerType === 'animator'
                            ? 'border-secondary bg-secondary/5 text-secondary'
                            : 'border-border hover:border-secondary/30'
                        }`}
                      >
                        <Users className="w-5 h-5" />
                        <span className="font-medium">Staff Member</span>
                      </button>
                    </div>
                  </div>

                  {/* Access Code — Staff Members (any role: admin, chief_animator, animator, kids_club_ops, limited_editor) */}
                  {registerType === 'animator' && (
                    <div className="bg-secondary/5 border border-secondary/20 rounded-xl p-4 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Lock className="w-5 h-5 text-secondary" />
                        <Label className="font-semibold">Access Code *</Label>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        Paste the code you received from the Owner (Admin) or your Admin/HOD (other staff).
                      </p>
                      <div className="flex gap-2">
                        <Input
                          id="access-code"
                          data-testid="register-access-code-input"
                          type="text"
                          placeholder="Enter your access code"
                          value={registerData.access_code}
                          onChange={(e) => {
                            setRegisterData({ ...registerData, access_code: e.target.value });
                            setCodeValidation({ checked: false, valid: false, message: '', target_email: null, hotel_name: null, hotel_id: null, code_type: null });
                          }}
                          required={registerType === 'animator'}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          onClick={validateAccessCode}
                          disabled={!registerData.access_code}
                          data-testid="validate-code-btn"
                          variant={codeValidation.valid ? "outline" : "default"}
                        >
                          {codeValidation.valid ? <Check className="w-4 h-4 text-green-600" /> : 'Verify'}
                        </Button>
                      </div>
                      {codeValidation.checked && (
                        <p className={`text-sm mt-2 flex items-center gap-1 ${codeValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                          {codeValidation.valid ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                          {codeValidation.message}
                        </p>
                      )}
                      {codeValidation.valid && codeValidation.hotel_name && (
                        <div className="mt-3 rounded-lg bg-white/70 border border-secondary/20 p-3 text-sm" data-testid="register-code-hotel-summary">
                          <p className="text-muted-foreground text-xs uppercase tracking-widest">This code activates</p>
                          <p className="mt-1"><span className="text-muted-foreground">Hotel:</span> <b>{codeValidation.hotel_name}</b></p>
                          {codeValidation.code_type && (
                            <p><span className="text-muted-foreground">Role:</span> <b className="capitalize">{String(codeValidation.code_type).replace('_', ' ')}</b></p>
                          )}
                          {codeValidation.target_email && (
                            <p className="break-all"><span className="text-muted-foreground">Email:</span> <span className="font-mono">{codeValidation.target_email}</span></p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="reg-name">{t('fullName')}</Label>
                    <Input
                      id="reg-name"
                      data-testid="register-name-input"
                      type="text"
                      placeholder="John Doe"
                      value={registerData.full_name}
                      onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                      required
                      readOnly={!!codeValidation.target_email}
                      className={`mt-1 ${codeValidation.target_email ? 'bg-muted text-muted-foreground' : ''}`}
                    />
                    {codeValidation.target_email && codeValidation.hotel_name && (
                      <p className="text-xs text-green-700 mt-1" data-testid="register-name-locked-hint">
                        Locked to the hotel display name from the invite.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="reg-email">{t('email')}</Label>
                    <Input
                      id="reg-email"
                      data-testid="register-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      readOnly={!!codeValidation.target_email}
                      className={`mt-1 ${codeValidation.target_email ? 'bg-muted text-muted-foreground font-mono' : ''}`}
                    />
                    {codeValidation.target_email && (
                      <p className="text-xs text-green-700 mt-1" data-testid="register-email-locked-hint">
                        This invite code is bound to <span className="font-mono">{codeValidation.target_email}</span>.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="reg-password">{t('password')}</Label>
                    <div className="relative mt-1">
                      <Input
                        id="reg-password"
                        data-testid="register-password-input"
                        type={showRegisterPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        data-testid="register-toggle-password"
                      >
                        {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Guest-specific fields */}
                  {registerType === 'guest' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="reg-nationality">Nationality</Label>
                          <div className="mt-1">
                            <CountrySelect
                              id="reg-nationality"
                              testId="register-nationality-select"
                              value={registerData.nationality}
                              onChange={(v) => setRegisterData({ ...registerData, nationality: v })}
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="reg-room">{t('roomNumber')}</Label>
                          <Input
                            id="reg-room"
                            data-testid="register-room-input"
                            type="text"
                            inputMode="numeric"
                            placeholder="1024"
                            value={registerData.room_number}
                            onChange={(e) => setRegisterData({ ...registerData, room_number: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      {/* Guest registration v2 — main guest + occupants */}
                      <div className="space-y-3 p-3 rounded-xl bg-secondary/5 border border-secondary/10" data-testid="guest-room-block">
                        <div>
                          <Label htmlFor="reg-main-guest" className="text-xs font-bold uppercase tracking-wider">{t('mainGuestName')}</Label>
                          <Input
                            id="reg-main-guest"
                            data-testid="register-main-guest-input"
                            type="text"
                            placeholder="John Smith"
                            value={registerData.main_guest_name}
                            onChange={(e) => setRegisterData({ ...registerData, main_guest_name: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider">
                            People in Room ({registerData.occupants.length} of 4)
                          </Label>
                          <div className="space-y-2 mt-1">
                            {registerData.occupants.map((occ, idx) => (
                              <div key={idx} className="flex gap-2" data-testid={`register-occupant-row-${idx}`}>
                                <Input
                                  type="text"
                                  placeholder={`Person ${idx + 1} full name`}
                                  value={occ.name}
                                  onChange={(e) => {
                                    const next = [...registerData.occupants];
                                    next[idx] = { ...next[idx], name: e.target.value };
                                    setRegisterData({ ...registerData, occupants: next });
                                  }}
                                  className="flex-1"
                                  data-testid={`register-occupant-name-${idx}`}
                                />
                                <select
                                  value={occ.category}
                                  onChange={(e) => {
                                    const next = [...registerData.occupants];
                                    next[idx] = { ...next[idx], category: e.target.value };
                                    setRegisterData({ ...registerData, occupants: next });
                                  }}
                                  className="rounded-full border border-input bg-background px-3 text-sm font-medium"
                                  data-testid={`register-occupant-category-${idx}`}
                                >
                                  <option value="adult">Adult</option>
                                  <option value="kid">Kid</option>
                                </select>
                                {registerData.occupants.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const next = registerData.occupants.filter((_, i) => i !== idx);
                                      setRegisterData({ ...registerData, occupants: next });
                                    }}
                                    className="px-2 text-muted-foreground hover:text-destructive"
                                    data-testid={`register-occupant-remove-${idx}`}
                                    aria-label="Remove"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ))}
                            {registerData.occupants.length < 4 ? (
                              <button
                                type="button"
                                onClick={() => setRegisterData({
                                  ...registerData,
                                  occupants: [...registerData.occupants, { name: '', category: 'adult' }],
                                })}
                                className="text-xs font-bold uppercase tracking-wider text-secondary hover:underline"
                                data-testid="register-add-occupant-btn"
                              >
                                + Add another person
                              </button>
                            ) : (
                              <p className="text-[11px] text-muted-foreground italic">
                                Need to register more than 4 guests? Please contact the Entertainment Team.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="reg-checkin">{t('checkInDate')} <span className="text-red-500">*</span></Label>
                          <Input
                            id="reg-checkin"
                            data-testid="register-checkin-input"
                            type="date"
                            aria-required="true"
                            value={registerData.check_in_date}
                            onChange={(e) => {
                              setRegisterData({ ...registerData, check_in_date: e.target.value });
                              if (fieldErrors.check_in_date) setFieldErrors({ ...fieldErrors, check_in_date: undefined });
                            }}
                            className={`mt-1 ${fieldErrors.check_in_date ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                            aria-invalid={!!fieldErrors.check_in_date}
                            aria-describedby="reg-checkin-error"
                          />
                          {fieldErrors.check_in_date && (
                            <p id="reg-checkin-error" className="text-xs text-red-500 mt-1" data-testid="register-checkin-error">
                              {fieldErrors.check_in_date}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="reg-checkout">{t('checkOutDate')} <span className="text-red-500">*</span></Label>
                          <Input
                            id="reg-checkout"
                            data-testid="register-checkout-input"
                            type="date"
                            aria-required="true"
                            value={registerData.check_out_date}
                            onChange={(e) => {
                              setRegisterData({ ...registerData, check_out_date: e.target.value });
                              if (fieldErrors.check_out_date) setFieldErrors({ ...fieldErrors, check_out_date: undefined });
                            }}
                            className={`mt-1 ${fieldErrors.check_out_date ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                            aria-invalid={!!fieldErrors.check_out_date}
                            aria-describedby="reg-checkout-error"
                          />
                          {fieldErrors.check_out_date && (
                            <p id="reg-checkout-error" className="text-xs text-red-500 mt-1" data-testid="register-checkout-error">
                              {fieldErrors.check_out_date}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="repeater"
                          data-testid="register-repeater-checkbox"
                          checked={registerData.repeater_guest}
                          onChange={(e) => setRegisterData({ ...registerData, repeater_guest: e.target.checked })}
                          className="rounded"
                        />
                        <Label htmlFor="repeater" className="text-sm">I'm a repeater guest</Label>
                      </div>
                    </>
                  )}

                  <Button
                    type="submit"
                    data-testid="register-submit-btn"
                    className="w-full h-12 font-bold text-base"
                    size="lg"
                    disabled={loading || (registerType === 'animator' && !codeValidation.valid)}
                  >
                    {loading ? t('creatingAccount') : t('createAccount')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AuthPage;
